import React, { useCallback, useState } from "react";
import Alert from "@amzn/meridian/alert";
import Button from "@amzn/meridian/button";
import Select, { SelectOption } from "@amzn/meridian/select";
import Theme from "@amzn/meridian/theme";
import { useTranslation } from "react-i18next";

import { useGetListSupportedYardIdentifiersQuery } from "@/features/relayYardControl/services/yardsApis";
import { trackRyteUiEvent } from "@/features/ryteTracker/utils/telemetry/trackRyteUiEvent";
import { nameSpaces } from "@/i18n";
import { useRyteView } from "@/pages/ryteTracker/RyteView/hooks/view/useRyteView";
import { keys } from "@/pages/ryteTracker/i18n";

export const RadSiteSelector = () => {
  const { t } = useTranslation(nameSpaces.ryteTracker);
  const { site, handleSiteChange } = useRyteView();
  const [searchQuery, setSearchQuery] = useState<string>();
  const {
    data: supportedYardIds,
    isLoading,
    error,
    refetch,
  } = useGetListSupportedYardIdentifiersQuery({});

  const buildingCodes =
    supportedYardIds?.yardIdentifiers?.flatMap(({ buildingCodes }) => buildingCodes || []).sort() ||
    [];
  const normalizedSearch = searchQuery?.toLowerCase();
  const matchedCodes = normalizedSearch
    ? buildingCodes.filter((code) => code.toLowerCase().includes(normalizedSearch))
    : buildingCodes;

  const valueLabel = useCallback(() => site || "", [site]);
  const handleChange = useCallback(
    (newSite: string) => {
      trackRyteUiEvent("ryte_site_changed", {
        trigger: "select",
        site: newSite,
        previousSite: site || "",
      });
      handleSiteChange(newSite);
    },
    [handleSiteChange, site]
  );
  const handleRetry = useCallback(() => {
    trackRyteUiEvent("ryte_site_refetch");
    refetch();
  }, [refetch]);

  if (isLoading) return null;

  if (error) {
    return (
      <>
        <Alert type="error" size="small" data-testid="rad-site-selector-error">
          {t(keys.RYTE_SITES_ERROR)}
        </Alert>
        <Button
          type="secondary"
          size="small"
          onClick={handleRetry}
          data-testid="rad-site-selector-retry"
        >
          {t(keys.RYTE_CHART_RETRY)}
        </Button>
      </>
    );
  }

  return (
    <Theme mode="light">
      <Select
        searchQuery={searchQuery}
        value={site || ""}
        onSearch={setSearchQuery}
        onChange={handleChange}
        placeholder={t(keys.RYTE_SITE_PLACEHOLDER)}
        width={300}
        size="small"
        valueLabel={valueLabel}
        popoverMaxHeight={500}
        aria-label={t(keys.RYTE_SITE)}
        data-testid="rad-site-selector"
      >
        {matchedCodes.length > 0 ? (
          matchedCodes.map((code, index) => (
            <SelectOption key={`${code}_${index}`} value={code} label={code} />
          ))
        ) : (
          <SelectOption value="" label={t(keys.RYTE_NO_SITES_FOUND)} disabled />
        )}
      </Select>
    </Theme>
  );
};
